import { useEffect, useState } from "react";
import { Box, Grid, Button, FormControl, FormHelperText, TextField, IconButton, CircularProgress } from "@mui/material";
import { ControllerRenderProps, FieldValues } from "react-hook-form";
import { DeleteOutlineOutlined } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";

import { FeatureComponentStyle } from "./style";

import { TextFieldSchema } from "@/types/form-generator-schema-type";
import FormImage from "@/components/kit/FormGenerator/FormImage";
import { useCreateImage } from "@/api/hooks/useImage";

interface IFeatures {
    field: ControllerRenderProps<FieldValues, string>;
    className?: string;
    errorMessage: string;
    props?: TextFieldSchema["props"];
}

const availableLangs = [
    { name: "en", placeholder: "En Feature" },
    { name: "ar", placeholder: "Ar Feature" },
];

const defaultValues: { en: string, ar: string, icon: string } = { en: "", ar: "", icon: "" };

export default function Features(props: IFeatures) {
    const [edit, setEdit] = useState<number | null>(null);
    const [values, setValues] = useState<{ en: string, ar: string, icon: string }[]>([]);
    const [inputs, setInputs] = useState(defaultValues);
    const [error, setError] = useState("");
    const { field, className, errorMessage } = props;
    const { name, onChange, onBlur, ref, value } = field;
    const { mutateAsync: uploadImage, isPending: isUploading } = useCreateImage();
    const [lastFile, setLastFile] = useState<File | null>(null);
    const [resetImageKey, setResetImageKey] = useState(0);

    useEffect(() => setValues(value), [value]);

    const handleEdit = (index: number) => {
        setEdit(index);
        setInputs(values[index]);
        setResetImageKey((prev) => prev + 1);
    };

    const handleClick = () => {
        if (inputs.en === "" || inputs.ar === "" || inputs.icon === "") {
            setError("All fields are required");
            return;
        } else {
            setError("");
        }

        setValues((prev) => {
            const newList = [...prev];

            if (edit === 0 || edit) {
                newList[edit] = inputs;
            } else {
                newList.push(inputs);
            }

            onChange({ target: { name, value: newList } });

            return newList;
        });
        setInputs(defaultValues);
        setEdit(null);
        setResetImageKey((prev) => prev + 1);
    };

    const handleDelete = (index: number) => {
        setValues((prevValues) => {
            const prevVal = [...prevValues];

            prevVal.splice(index, 1);
            onChange({ target: { name, value: prevVal } });

            return prevVal;
        });
    };

    const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const name = e.target.name;
        setInputs((prev) => ({ ...prev, [name]: val }));
    };

    const handleIconChange = async (file: File | undefined) => {
        if (!file) return;
        if (file.type === "image/svg+xml") {
            setError("SVG files are not allowed. Please upload a JPG, PNG, or WEBP image.");
            return;
        }
        setLastFile(file);
        setError("");
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("title", file.name);
            formData.append("type", "plan_icon");
            await uploadIcon(formData);
        } catch (e) {
            setError("Icon upload failed");
        }
    };

    const uploadIcon = async (formData: FormData) => {
        const res = await uploadImage({ body: formData });
        setInputs((prev) => ({ ...prev, icon: res.data.url }));
        setError("");
    };

    const handleRetry = async () => {
        if (lastFile) {
            setError("");
            try {
                const formData = new FormData();
                formData.append("file", lastFile);
                formData.append("title", lastFile.name);
                formData.append("type", "plan_icon");
                await uploadIcon(formData);
            } catch (e) {
                setError("Icon upload failed");
            }
        }
    };

    // Add a handler to clear icon and stop upload
    const handleRemoveIcon = () => {
        setInputs((prev) => ({ ...prev, icon: "" }));
        setLastFile(null);
        // There is no direct way to cancel an ongoing upload, but we can ignore the result
        // Optionally, you can add a flag to ignore upload result if needed
    };

    return (
        <FeatureComponentStyle>
            <FormControl fullWidth className={className} margin="normal" variant="outlined">
                <Box className="content">
                    <Grid container alignItems="center" spacing={1}>
                        {availableLangs.map((item) => {
                            const { name: lang, placeholder } = item;

                            return (
                                <Grid key={lang} item md={5} xs={12}>
                                    <TextField
                                        ref={ref}
                                        fullWidth
                                        multiline
                                        label={placeholder}
                                        maxRows={5}
                                        minRows={3}
                                        name={lang}
                                        placeholder={placeholder}
                                        sx={{ direction: `${lang === "ar" ? "rtl" : "ltr"}` }}
                                        value={inputs[lang as "ar" | "en"]}
                                        variant="outlined"
                                        onBlur={onBlur}
                                        onChange={handleLocalChange}
                                        {...props}
                                    />
                                </Grid>
                            );
                        })}
                        <Grid item md={5} xs={12}>
                            <FormImage
                                key={resetImageKey}
                                field={{
                                    value: inputs.icon || undefined,
                                    onChange: (file: File) => handleIconChange(file),
                                    name: "icon-upload",
                                    onBlur: () => {},
                                    ref: () => {},
                                }}
                                label="Icon"
                                placeholder="Upload icon image (3MB max)"
                                errorMessage={{} as any}
                                props={{
                                    accept: "image/*",
                                    previewImage: inputs.icon || undefined,
                                    onRemove: handleRemoveIcon,
                                }}
                                className="icon-upload"
                            />
                            {isUploading && (
                                <Box mt={1} display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={24} />
                                    <span>Uploading...</span>
                                </Box>
                            )}
                            {error && !isUploading && (
                                <Box mt={1} display="flex" alignItems="center" gap={1}>
                                    {lastFile && error === "Icon upload failed" && (
                                        <Button size="small" variant="outlined" onClick={handleRetry}>
                                            Retry
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {inputs.icon && (
                                <Box mt={1}>
                                    <img
                                        src={inputs.icon}
                                        alt="icon preview"
                                        style={{ maxHeight: 40, maxWidth: 40, borderRadius: 8 }}
                                    />
                                </Box>
                            )}
                        </Grid>
                        <Grid item md={2} sx={{ alignItems: "center" }} xs={12}>
                            <Button
                                className="add-btn"
                                color="primary"
                                variant="contained"
                                onClick={handleClick}
                                disabled={isUploading}
                            >
                                {edit || edit === 0 ? "Update" : "Add"}
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            {error && <FormHelperText error>{error}</FormHelperText>}
                        </Grid>
                        <Grid item xs={12}>
                            {errorMessage ? <FormHelperText error>{errorMessage}</FormHelperText> : null}
                        </Grid>
                    </Grid>
                </Box>
            </FormControl>
            <Box className="features-list">
                {values.map((item, index) => {
                    const { en, ar, icon } = item;
                    return (
                        <Box key={en + icon} className="features-item">
                            <Box className="content">
                                <p className="en text">
                                    <b>En:</b> <span style={{ marginLeft: 4 }}>{en}</span>
                                </p>
                                <p className="ar text">
                                    <b>Ar: </b>
                                    <span style={{ marginLeft: 4 }}>{ar}</span>
                                </p>
                                {icon && (
                                    <p className="icon text">
                                        <b>Icon: </b>
                                        <span>
                                            <img
                                                src={icon}
                                                alt="icon"
                                                style={{
                                                    maxHeight: 24,
                                                    maxWidth: 24,
                                                    verticalAlign: "middle",
                                                    marginLeft: 4,
                                                }}
                                            />
                                        </span>
                                    </p>
                                )}
                            </Box>
                            <Box className="actions">
                                <IconButton className="icon-btn" onClick={() => handleEdit(index)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton className="icon-btn" onClick={() => handleDelete(index)}>
                                    <DeleteOutlineOutlined />
                                </IconButton>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </FeatureComponentStyle>
    );
}
